import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import React, { useContext, useMemo } from 'react'

import { type IconProp } from 'slab/types'

import { ButtonGroupContext } from './ButtonGroup'

export interface BaseButtonProps {
  children: React.ReactNode;
  disabled?: boolean;
  leftIcon?: IconProp;
  rightIcon?: IconProp;
  size?: 'large' | 'small';
  variant?: 'primary' | 'primary-destructive' | 'secondary' | 'tertiary' | 'tertiary-deemphasized' | 'tertiary-destructive';
  width?: 'auto' | 'full';
}

const buttonClassNames = cva(
  'appearance-none group inline-flex justify-center gap-2 items-center rounded-lg body-bold outline outline-offset-2 outline-2 outline-transparent focus-visible:outline-thatch-500 transition aria-disabled:pointer-events-none text-nowrap',
  {
    variants: {
      size: {
        large: 'h-10 py-2 px-4',
        small: 'h-8 py-1 px-4',
      },
      variant: {
        primary:
          'border-thatch-700 border border-b-2 hover:border-thatch-800 focus-visible:border-thatch-800 disabled:border-thatch-400 aria-disabled:border-thatch-400 bg-thatch-500 hover:bg-thatch-600 focus-visible:bg-thatch-600 disabled:bg-thatch-400 aria-disabled:bg-thatch-400 bg-clip-padding',
        'primary-destructive':
          'border-red-500 border border-b-2 hover:border-red-600 focus-visible:border-red-600 disabled:border-red-300 aria-disabled:border-red-300 bg-red-400 hover:bg-red-500 focus-visible:bg-red-500 disabled:bg-red-300 aria-disabled:bg-red-300 bg-clip-padding',
        secondary:
          'border-gray-300 border border-b-2 hover:border-gray-400 focus-visible:border-gray-400 disabled:border-gray-200 aria-disabled:border-gray-200 bg-white hover:bg-gray-100 focus-visible:bg-gray-50 disabled:bg-gray-200 aria-disabled:bg-gray-200 bg-clip-padding',
        tertiary:
          'text-thatch-500 hover:text-thatch-600 disabled:text-thatch-400 aria-disabled:text-thatch-400 hover:bg-thatch-100 focus-visible:bg-thatch-100 active:bg-thatch-50',
        'tertiary-deemphasized':
          'text-gray-500 hover:text-gray-600 focus-visible:text-gray-600 disabled:text-gray-300 aria-disabled:text-gray-300 hover:bg-gray-100 focus-visible:bg-gray-100 active:bg-gray-50',
        'tertiary-destructive':
          'text-red-500 hover:text-red-600 disabled:text-red-400 aria-disabled:text-red-400 hover:bg-red-100 focus-visible:bg-red-100 active:bg-red-50',
      },
      width: {
        auto: undefined,
        full: 'w-full',
      },
    },
    compoundVariants: [
      {
        variant: ['primary', 'primary-destructive'],
        class:
          'text-white hover:text-white disabled:text-white/80 aria-disabled:text-white/80 bg-gradient-to-b from-black/20 disabled:bg-none aria-disabled:bg-none shadow-[0_1px_0_1px_rgba(255,255,255,.25)_inset] active:shadow-[0_1px_0_0_rgba(255,255,255,.25)_inset] disabled:shadow-[0_1px_0_1px_rgba(255,255,255,.25)_inset]',
      },
      {
        variant: ['secondary'],
        class:
          'text-gray-800 hover:text-gray-800 disabled:text-gray-800/50 aria-disabled:text-gray-800/50 bg-gradient-to-b from-black/5 to-50% disabled:bg-none aria-disabled:bg-none shadow-[0_1px_0_1px_rgba(255,255,255,.6)_inset] active:shadow-[0_1px_0_0_rgba(255,255,255,.6)_inset] disabled:shadow-[0_1px_0_1px_rgba(255,255,255,.6)_inset]',
      },
      {
        variant: ['tertiary', 'tertiary-deemphasized', 'tertiary-destructive'],
        class:
          'bg-transparent disabled:bg-transparent aria-disabled:bg-transparent',
      },
    ],
  }
)

const iconClassNames = cva('flex items-center h-4 transition-colors', {
  variants: {
    variant: {
      primary: undefined,
      'primary-destructive': undefined,
      secondary: 'text-gray-500',
      tertiary: 'text-thatch-400',
      'tertiary-deemphasized': 'text-gray-400',
      'tertiary-destructive': 'text-red-500',
    },
  },
  compoundVariants: [
    {
      variant: [
        'secondary',
        'tertiary',
        'tertiary-deemphasized',
        'tertiary-destructive',
      ],
      class: 'group-disabled:text-inherit group-aria-disabled:text-inherit',
    },
  ],
})

export function BaseButton ({
  children,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  size = 'small',
  variant = 'secondary',
  width = 'auto',
  ...props
}: BaseButtonProps) {
  const { size: buttonGroupSize, width: buttonGroupWidth } = useContext(ButtonGroupContext)

  const appliedSize = buttonGroupSize ?? size
  const appliedWidth = buttonGroupWidth ?? width

  const buttonClassName = useMemo(
    () => buttonClassNames({ size: appliedSize, variant, width: appliedWidth }),
    [appliedSize, variant, appliedWidth]
  )
  const iconClassName = useMemo(() => iconClassNames({ variant }), [variant])

  return (
    <Slot
      {...props}
      className={buttonClassName}
      data-size={appliedSize}
      data-variant={variant}
      data-width={appliedWidth}
    >
      {LeftIcon && (
        <span className={iconClassName}>
          <LeftIcon size={16} />
        </span>
      )}
      <Slottable>
        {React.isValidElement(children) && (
          React.cloneElement(
            children,
            undefined,
            <span>{children.props.children}</span>
          )
        )}
      </Slottable>
      {RightIcon && (
        <span className={iconClassName}>
          <RightIcon size={16} />
        </span>
      )}
    </Slot>
  )
}
